import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Row, Switch } from "antd";
import { DetailListActions, DropdownActionConfig, PrimaryActionsConfig } from "Components/General/DetailListActions";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { SearchBar } from "Components/General/SearchBar";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { projectsDataStore } from "Stores";
import { EmailSendModal } from "../EmailSendModal/EmailSendModal";
import { OutlookCheckbox } from "../OutlookLayout/Components/OutlookCheckbox";
import { MailSortBy } from "../Stores/EmailTypeStore";
import { emailModalProps, emailSendModal } from "../utils/emailModalProps";
import { EmailInboxAdminTools } from "./EmailInboxFilters/EmailInboxAdminTools";
import { EmailSearchForm } from "./EmailSearchForm";
import { LayoutModeSelect } from "./LayoutModeSelect";
import { SortingDropdown } from "./SortingDropdown";

interface ActionsConfig {
  primaryActions: PrimaryActionsConfig[];
  dropdownActions?: DropdownActionConfig[];
}

interface SearchBarWithActionsProps extends WithTranslation {
  emailType: API_MAILS_TYPE;
  onLayoutChange: (layoutMode: API_USERDATA_LAYOUTTYPE) => void;
  layoutMode: API_USERDATA_LAYOUTTYPE;
  accessLevel: "private" | "project";
  onSearch: (text: string) => void;
  projectTopicInbox?: boolean;
  onSort: (sortBy: MailSortBy, sortOrder: API_MAILPAGINATION_SORTORDER) => void;
  sortedBy: MailSortBy;
  sortedOrder: API_MAILPAGINATION_SORTORDER;
  actionsConfig: ActionsConfig;
  onRefresh: () => void;
  isIntermediate: boolean;
  isSelectedAll: boolean;
  onSelectAll: () => void;
  isSearchBarVisible: boolean;
  switchProjectInboxes: (switchProject: boolean) => void;
}

export const SearchBarWithActions = withTranslation(I18N_NS.PROJECT)(
  observer(
    ({
      t,
      accessLevel,
      projectTopicInbox,
      onSearch,
      actionsConfig,
      onSort,
      sortedBy,
      sortedOrder,
      emailType,
      layoutMode,
      isIntermediate,
      isSelectedAll,
      onSelectAll,
      isSearchBarVisible: isVisible,
      switchProjectInboxes,
      ...props
    }: SearchBarWithActionsProps) => {
      const buffedActionsConfig: ActionsConfig = {
        ...actionsConfig,
        primaryActions: [
          {
            onClick: props.onRefresh,
            icon: ["far", "sync"] as IconProp,
            label: t("Refresh"),
          },
        ].concat(...(actionsConfig.primaryActions as Required<PrimaryActionsConfig>[])),
      };
      const isInboxSwitchVisible =
        projectsDataStore.currentProject.mailSettings?.projectInboxes &&
        accessLevel === "project" &&
        emailType === "INBOX" &&
        isVisible;
      const isNewEmailShow =
        !projectsDataStore.currentProject.archivProject &&
        (accessLevel === "private" ||
          (accessLevel === "project" && projectsDataStore.currentProject?.mailSettings.projectInbox === "WRITE"));

      return (
        <>
          <Row justify={isVisible ? "space-between" : "end"} style={{ flexWrap: "nowrap" }}>
            {isVisible ? (
              <ModalConsumer>
                {({ showModal }) => (
                  <SearchBar
                    hideClear={true}
                    placeholder={t("Search in subject, text")}
                    onShowSearchModal={() =>
                      showModal(EmailSearchForm, {
                        modalProps: { ...emailModalProps, title: t("Search emails") },
                      })
                    }
                    onSearch={onSearch}
                  />
                )}
              </ModalConsumer>
            ) : null}
            {isNewEmailShow ? (
              <ModalConsumer>
                {({ showModal }) => (
                  <Button
                    style={{ marginLeft: 20 }}
                    type="primary"
                    onClick={() =>
                      showModal(EmailSendModal, {
                        modalProps: { ...emailSendModal },
                        componentProps: {
                          onSubmitCallback: props.onRefresh,
                        },
                      })
                    }
                  >
                    {/*tslint:disable-next-line: max-line-length */}
                    <FontAwesomeIcon icon={["fas", "plus"]} size="1x" style={{ marginRight: "17px" }} />
                    {t("New email")}
                  </Button>
                )}
              </ModalConsumer>
            ) : null}
            {projectsDataStore.isAdmin && projectsDataStore.currentProject.mailSettings?.projectInboxes && (
              <EmailInboxAdminTools />
            )}
          </Row>
          <Row justify="space-between" align="middle">
            {layoutMode === "SPLIT" ? (
              <div className="mb-2" style={{ marginLeft: 1 }}>
                <div style={{ position: "relative", left: -13, top: -2, display: "inline-block" }}>
                  <OutlookCheckbox intermediate={isIntermediate} checked={isSelectedAll} onClick={onSelectAll} />
                </div>
                <DetailListActions {...buffedActionsConfig} />
              </div>
            ) : (
              <div className="mb-2" style={{ marginLeft: 10 }}>
                <DetailListActions {...buffedActionsConfig} />
              </div>
            )}
            {isInboxSwitchVisible ? (
              <Row>
                <Switch
                  defaultChecked={projectTopicInbox}
                  style={{ marginRight: 5 }}
                  onChange={(checked) => switchProjectInboxes(checked)}
                />
                {t("Also show emails of topic inboxes")}
              </Row>
            ) : null}
            <Row align="middle">
              <SortingDropdown onSort={onSort} sortedBy={sortedBy} sortedOrder={sortedOrder} emailType={emailType} />
              <LayoutModeSelect onLayoutChange={props.onLayoutChange} currentLayout={layoutMode} />
            </Row>
          </Row>
        </>
      );
    }
  )
);
